<script>
export default {
  name: 'LoginForm'
}
</script>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import router from '@/router/index.js';
import { consumePostLoginRedirect } from '@functions/postLoginRedirect';
import { useAuthStore } from '@stores/auth'
import { useRoute } from 'vue-router';
import { url } from '@/config/urls';
import deviceService from '@/services/deviceService.js';
import pushNotificationService from '@/services/pushNotificationService.js';

const isDisabledLogin = ref(false);
const isShowLoginErrorMessage = ref(false);
const loginErrorMessage = ref('');
const route = useRoute();

const isSsoLogin = computed(() => route.query.sso === 'true');

const formInputs = ref({
  loginId: '',
  password: '',
  keepLoggedIn: false
});

const errors = ref({
  loginId: '',
  password: ''
});

const getPlatform = () => {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'iOS';
  if (platform === 'android') return 'Android';
  return 'Web';
};

const registerMobileDeviceOnLogin = async () => {
  if (getPlatform() === 'Web') {
    return;
  }
  const deviceInfo = await deviceService.getDeviceInfo();
  const pushToken = await pushNotificationService.getPushToken();
  const platform = getPlatform();
  const uuidSuffix = (deviceInfo.uuid || '').slice(-4);

  await axios.post(url.api.mobileDevices.add, {
    uuid: deviceInfo.uuid,
    platform: (platform || '').substring(0, 10),
    model: `${platform} ${uuidSuffix}`.substring(0, 50),
    pushToken,
    doUpdateScannedAt: false,
  });
};

const passwordInputType = ref('password');
const isPasswordVisible = ref(false);
const passwordVisibleIcon = ref('icon_eye_slash1');

watch(() => passwordInputType.value, (inputType) => {
    passwordVisibleIcon.value = 'password' 
      === inputType ?  'icon_eye_slash1' : 'icon_eye1';
})

const isSetClassErrorLogin = computed({
  get() {
    if (errors.value.loginId) {
      return true;
    }else {
      if (isShowLoginErrorMessage.value) {
        return true;
      }
    }
    return false;
  }
})

const isSetClassErrorPassword = computed({
  get() {
    if (errors.value.password) {
      return true;
    }else {
      if (isShowLoginErrorMessage.value) {
        return true;
      }
    }
    return false;
  }
})

const switchPasswordVisibility = () => {
  if (isPasswordVisible.value) {
    passwordInputType.value = 'password';
    isPasswordVisible.value = false;
  }else {
    passwordInputType.value = 'text';
    isPasswordVisible.value = true;
  }
}

// バリデーション
const loginIdValidation = () => {
  const maxLength = 10;
  errors.value.loginId = '';

  if (formInputs.value.loginId) {
    if (maxLength < formInputs.value.loginId.length) {
      errors.value.loginId = 'ログインIDは10文字以内で入力してください。';
    }
  }else {
    errors.value.loginId = 'ログインIDを入力してください。';
  }
}

const passwordValidation = () => {
  const maxLength = 255;

  isShowLoginErrorMessage.value = false;
  loginErrorMessage.value = '';
  errors.value.password = '';

  if (formInputs.value.password) {
    if (maxLength < formInputs.value.password.length) {
      errors.value.password = 'パスワードは255文字以内で入力してください。';
    }
  }else {
    errors.value.password = 'パスワードを入力してください。';
  }
}

// ログイン処理
const login = async () => {
  isDisabledLogin.value = true
  try {
    const auth = useAuthStore()
    const result = await auth.login(
      {
        loginId: formInputs.value.loginId,
        password: formInputs.value.password,
        keepLoggedIn: formInputs.value.keepLoggedIn
      }
    )
    if (result?.status === 'FORCED_PASSWORD_CHANGE') {
      router.replace({
        path: '/change-password'
      })
      return
    }

    // モバイル端末の登録処理
    try {
      await registerMobileDeviceOnLogin();
    } catch (_) {
      // ログイン自体は継続する
    }

    if (isSsoLogin.value) {
      window.location.href = '/front/oauth2/authorize/continue'
    } else {
      const nextPath = consumePostLoginRedirect(router) ?? '/'
      router.replace(nextPath)
    }
  } catch (e) {
    const status = e?.response?.status;
    if (status === 401 || status === 423) {
      isShowLoginErrorMessage.value = true
      loginErrorMessage.value = e.response.data?.message || ''
    }
  } finally {
    isDisabledLogin.value = false
  }
}

const onClickLogin = () => {
  isDisabledLogin.value = true;
  loginIdValidation();
  passwordValidation();
  if (Object.values(errors.value).some(value => value != '')){
    isDisabledLogin.value = false;
    return;
  }

  login();
}

const removeReadonly = (event) => {
  event.target.removeAttribute('readonly');
};

onMounted(async() => {
  const auth = useAuthStore();
  await auth.initCsrf();
  if(auth.isAuthenticated) {
    const nextPath = consumePostLoginRedirect(router) ?? '/'
    router.replace(nextPath)
  }
})

</script>

<template>
<p class="copy1">ID・パスワードを入力して<br>ログインしてください。</p>

<form class="form-group" @submit.prevent="onClickLogin">
	<div>
    <input type="text" placeholder="ログインID" class="form-control com_form1 s1"
           v-model="formInputs.loginId" :class="{ error: isSetClassErrorLogin }"
           autocomplete="address-line3"
           enterkeyhint="send">
	<div class="com_validate_msg1 error" v-if="errors.loginId">{{ errors.loginId }}</div>
	</div>
	<div class="com_show_hide_password1 com_mgt15 com_mgt10s">
    <input :type="passwordInputType" placeholder="パスワード"
           class="form-control com_form1 s1" v-model="formInputs.password"
           :class="{ error: isSetClassErrorPassword }"
           autocomplete="address-line3"
           @keydown.enter="onClickLogin" enterkeyhint=”send”>
		<div class="com_show_hide_password_btn1"><a @click.prevent="switchPasswordVisibility"><i aria-hidden="true" :class="passwordVisibleIcon"></i></a></div>
	</div>
	<div class="com_validate_msg1 error" v-if="errors.password">{{ errors.password }}</div>
	<div class="com_validate_msg1 error" v-if="isShowLoginErrorMessage">{{ loginErrorMessage }}</div>
  <label class="copy2">
    <input type="checkbox" v-model="formInputs.keepLoggedIn" @keydown.enter="onClickLogin">
    <span>ログイン情報を保持する</span>
  </label>
	<button type="submit" class="btn com_btn3 cl1" @click.prevent="onClickLogin" :disabled="isDisabledLogin">ログイン</button>
	<p class="copy3">パスワードがわからない場合は<router-link to="/forgot-password">こちら</router-link></p>
</form>

</template>

<style scoped>
::-ms-reveal {
  display: none;
}
</style>
